/**
 * EmployerHomePage.js — RC 1.30.B Employer Organisation Model.
 *
 * First real employer-side surface. Reads the caller's organisation
 * from `/api/employer/org`:
 *   - 404  → render the create-org form
 *   - 200  → render the profile editor
 *   - 503  → migration 026 hasn't been applied yet; surface the
 *            honest "ask your admin to apply it" message
 *   - 403  → shouldn't be reachable (WorkspaceGuard gates
 *            `/employer`) but we handle it anyway for defence in
 *            depth
 *
 * Deliberately minimal. No fake metrics, no placeholder dashboards.
 * This page IS the employer workspace for RC 1.30.B — roles,
 * matching, and signals arrive in later slices.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle, Building2, Loader2, Save, ShieldAlert, Users as UsersIcon, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { canManageOrg, ROLE_LABEL } from '@/lib/employerAuthority';
import {
  ShieldCheck as ShieldCheckIcon, UserCheck as UserCheckIcon,
} from 'lucide-react';

const SIZE_BAND_OPTIONS = [
  { value: 'seed',       label: 'Seed (1–10)' },
  { value: 'growth',     label: 'Growth (11–50)' },
  { value: 'scale',      label: 'Scale (51–250)' },
  { value: 'enterprise', label: 'Enterprise (250+)' },
];

const LOCATION_MODE_OPTIONS = [
  { value: '__none__',       label: '— not set —' },
  { value: 'remote_global',  label: 'Remote — global' },
  { value: 'remote_region',  label: 'Remote — specific region' },
  { value: 'hub',            label: 'Hub-based' },
  { value: 'office',         label: 'In-office' },
];

const WORKING_MODEL_OPTIONS = [
  { value: '__none__',    label: '— not set —' },
  { value: 'async',       label: 'Async-first' },
  { value: 'deep_work',   label: 'Deep-work focused' },
  { value: 'collab',      label: 'High collaboration' },
  { value: 'flexible',    label: 'Flexible hours' },
];

const EMPTY_FORM = {
  name:         '',
  sector:       '',
  size_band:    '',
  website:      '',
  description:  '',
  culture_json: {
    working_model: '',
    location_mode: '',
    why_join_us:   '',
  },
};

function normaliseOrg(org) {
  if (!org) return { ...EMPTY_FORM };
  const c = org.culture_json || {};
  return {
    name:        org.name        || '',
    sector:      org.sector      || '',
    size_band:   org.size_band   || '',
    website:     org.website     || '',
    description: org.description || '',
    culture_json: {
      working_model: c.working_model || '',
      location_mode: c.location_mode || '',
      why_join_us:   c.why_join_us   || '',
    },
  };
}

function diffForm(server, form) {
  // Returns the partial patch body to POST/PATCH. Culture keys sent
  // individually so the server can merge them (empty → clear).
  const out = {};
  const keys = ['name', 'sector', 'size_band', 'website', 'description'];
  for (const k of keys) {
    if ((server?.[k] || '') !== (form?.[k] || '')) {
      out[k] = form?.[k] || '';
    }
  }
  const sc = server?.culture_json || {};
  const fc = form?.culture_json   || {};
  const cPatch = {};
  for (const k of ['working_model', 'location_mode', 'why_join_us']) {
    if ((sc[k] || '') !== (fc[k] || '')) cPatch[k] = fc[k] || '';
  }
  if (Object.keys(cPatch).length) out.culture_json = cPatch;
  return out;
}


export default function EmployerHomePage() {
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [unmigrated, setUnmigrated] = useState(false);
  const [forbidden, setForbidden]   = useState(false);

  const [server, setServer]   = useState(null);
  const [form, setForm]       = useState({ ...EMPTY_FORM });
  const [saving, setSaving]   = useState(false);
  // RC 1.61 P1.2 (operator-approved 2026-05-29): member role is now
  // hydrated to drive read-only gating for non-owners. Backend RLS
  // already blocks the underlying write, but the UI used to render
  // an editable form + Save button → confused recruiters who could
  // click but couldn't save. The mutate controls are now hidden
  // when `canManageOrg(memberRole)` is false.
  const [memberRole, setMemberRole] = useState(null);

  // IA cleanup (2026-02-26): accountId / memberRole were previously
  // hydrated for the now-removed EmployerDashboardPanel mount. The
  // Organisation page is no longer the home for operational
  // telemetry — that lives on /employer/account. Members CTA +
  // Governance hub card below use static deep-links and do not
  // need either value.

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUnmigrated(false);
    setForbidden(false);
    try {
      // Phase 0.4 (C-2 = 2b lift): read source switched from legacy
      // /api/employer/org to the new Phase 0.3 /api/employer/v1/me
      // endpoint. Both return { organisation: <row> } with the same
      // shape; the lift future-proofs for the eventual emp_accounts
      // rename. Write paths (POST/PATCH) intentionally stay on
      // /api/employer/org — Phase 0.4 out-of-scope contract.
      const data = await apiFetch('/api/employer/v1/me');
      const org = data?.organisation || null;
      setServer(org);
      setForm(normaliseOrg(org));
      // RC 1.61 P1.2: capture the caller's member role so the
      // editor can render read-only for non-owners. The role lives
      // under `member.role` per the /api/employer/v1/me schema —
      // mirrors how EmployerAccountPage hydrates it.
      setMemberRole(
        data?.member?.role
        || data?.member_role
        || null,
      );
    } catch (err) {
      if (err?.status === 404) {
        setServer(null);
        setForm({ ...EMPTY_FORM });
      } else if (err?.status === 403) {
        setForbidden(true);
      } else if (err?.status === 503) {
        setUnmigrated(true);
      } else {
        setError(err?.message || 'Failed to load organisation.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isCreate = !server;
  const patch = useMemo(
    () => (isCreate ? null : diffForm(normaliseOrg(server), form)),
    [server, form, isCreate],
  );
  const isDirty = isCreate
    ? Boolean(form.name.trim() && form.description.trim())
    : Object.keys(patch || {}).length > 0;

  const onSave = async () => {
    if (saving || !isDirty) return;
    setSaving(true);
    try {
      if (isCreate) {
        // POST the full create body (only keys with content).
        const body = {
          name:         form.name.trim(),
          description:  form.description.trim(),
        };
        if (form.sector.trim())    body.sector    = form.sector.trim();
        if (form.size_band)        body.size_band = form.size_band;
        if (form.website.trim())   body.website   = form.website.trim();
        const c = {};
        if (form.culture_json.working_model) c.working_model = form.culture_json.working_model;
        if (form.culture_json.location_mode) c.location_mode = form.culture_json.location_mode;
        if (form.culture_json.why_join_us.trim()) c.why_join_us = form.culture_json.why_join_us.trim();
        if (Object.keys(c).length) body.culture_json = c;
        const resp = await apiFetch('/api/employer/org', { method: 'POST', body });
        const org = resp?.organisation || null;
        setServer(org);
        setForm(normaliseOrg(org));
        toast.success('Organisation created.');
      } else {
        const resp = await apiFetch('/api/employer/org', {
          method: 'PATCH',
          body: patch,
        });
        const org = resp?.organisation || null;
        setServer(org);
        setForm(normaliseOrg(org));
        toast.success('Organisation saved.');
      }
    } catch (err) {
      toast.error(err?.data?.detail || err?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const onRevert = () => {
    setForm(normaliseOrg(server));
  };

  // ── States ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="employer-home-loading">
        <Loader2 className="h-5 w-5 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="employer-home-forbidden"
      >
        <ShieldAlert className="mx-auto mb-4 h-10 w-10 text-slate-300" />
        <h1 className="text-xl font-semibold text-ndor-ink">Employer access only</h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">
          Your account doesn't have employer workspace access. Ask an
          admin to grant it.
        </p>
      </div>
    );
  }

  if (unmigrated) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="employer-home-unmigrated"
      >
        <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-amber-500" />
        <h1 className="text-xl font-semibold text-ndor-ink">Setup pending</h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">
          The employer organisations table hasn't been set up yet.
          An admin needs to apply migration{' '}
          <code className="font-mono">026_employer_organisations.sql</code>{' '}
          via the Supabase SQL editor.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="mx-auto max-w-xl py-24 px-4 text-center"
        data-testid="employer-home-error"
      >
        <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-rose-500" />
        <h1 className="text-xl font-semibold text-ndor-ink">Couldn't load organisation</h1>
        <p className="mt-2 text-sm text-ndor-ink-muted">{error}</p>
        <Button variant="outline" className="mt-6" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }

  // ── Editor / Create form ────────────────────────────────────

  return (
    <div
      className="mx-auto max-w-3xl py-8 px-4 space-y-6"
      data-testid={isCreate ? 'employer-home-create' : 'employer-home-editor'}
    >
      <div>
        <h1 className="text-2xl font-semibold text-ndor-ink flex items-center gap-2">
          <Building2 className="h-5 w-5 text-ndor-ink-muted" />
          {isCreate ? 'Create your organisation' : 'Your organisation profile'}
        </h1>
        <p className="mt-1 text-sm text-ndor-ink-muted">
          {isCreate
            ? 'This is the start of your employer workspace. Fill in a few basics — you can refine everything later.'
            : 'This information is shown to candidates when they receive a signal from your organisation.'}
        </p>
      </div>

      {/* IA cleanup (2026-02-26): EmployerDashboardPanel removed
          from this surface. Operational telemetry (counters, plan
          & usage, audit ledger) now lives exclusively on
          /employer/account ("Account & usage"). The Organisation
          page is the candidate-visible employer identity surface;
          it must not host internal operational data.
          Doctrine: 1 user owns 0–1 orgs, member of N orgs.
          TODO future pass: hide mutating organisation controls for
          non-owner members (POST/PATCH /api/employer/org is owner
          RLS-gated server-side; UI should match by role). */}

      {!isCreate && (
        <div
          className="rounded-md border border-ndor-edge bg-ndor-surface-alt px-4 py-3 text-xs text-ndor-ink-secondary leading-relaxed"
          data-testid="employer-profile-callout"
        >
          Candidates see your organisation name, sector, stage,
          description, and website when reviewing a signal.
        </div>
      )}

      {/* RC 1.61 P1.2 (operator-approved 2026-05-29) — non-owner
          hardening. The Edit-profile mutate path is owner/admin-only
          (A1 authority). Backend RLS blocks the underlying writes,
          so the form here is rendered read-only and the
          Save/Revert footer is suppressed. The doctrine matrix
          lives in `lib/employerAuthority.js`. */}
      {memberRole && !canManageOrg(memberRole) ? (
        <div
          className="text-[12px] text-ndor-ink-muted bg-ndor-surface-alt/40 border border-ndor-edge rounded px-3 py-2"
          data-testid="employer-org-readonly-banner"
        >
          View-only access — your role
          {' '}<strong>{ROLE_LABEL[memberRole] || memberRole}</strong>{' '}
          cannot edit organisation details. Ask an owner or admin
          to make changes.
        </div>
      ) : null}

      <Card className="border-ndor-edge shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-ndor-ink">
            The basics
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <Label>Organisation name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Acme Robotics"
              className="h-10"
              data-testid="employer-field-name"
              autoComplete="off"
            />
            <p className="text-[11px] text-ndor-ink-muted">
              The name candidates will see.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Sector</Label>
              <Input
                value={form.sector}
                onChange={(e) => setForm((f) => ({ ...f, sector: e.target.value }))}
                placeholder="e.g. Industrial automation, Fintech"
                className="h-10"
                data-testid="employer-field-sector"
                autoComplete="off"
              />
              <p className="text-[11px] text-ndor-ink-muted">
                e.g. Industrial automation, Fintech.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Stage</Label>
              <Select
                value={form.size_band || '__none__'}
                onValueChange={(v) => setForm((f) => ({
                  ...f, size_band: v === '__none__' ? '' : v,
                }))}
              >
                <SelectTrigger className="h-10" data-testid="employer-field-size-band">
                  <SelectValue placeholder="Select stage…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— not set —</SelectItem>
                  {SIZE_BAND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-ndor-ink-muted">
                Helps candidates understand your context.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Website</Label>
            <Input
              value={form.website}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
              placeholder="https://acme.example"
              className="h-10"
              data-testid="employer-field-website"
              autoComplete="off"
            />
            <p className="text-[11px] text-ndor-ink-muted">
              Shown as a link to candidates (optional). Include the full
              URL starting with https://.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>About your organisation *</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="A short about — what you do and who you serve."
              rows={4}
              data-testid="employer-field-description"
            />
            <p className="text-[11px] text-ndor-ink-muted">
              Your voice. Be direct and honest. 200 characters recommended.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-ndor-edge shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-ndor-ink">
            Culture & working model
          </CardTitle>
          <p className="text-xs text-ndor-ink-muted pt-1">
            Lightweight signals candidates may use as context. Optional.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Working model</Label>
              <Select
                value={form.culture_json.working_model || '__none__'}
                onValueChange={(v) => setForm((f) => ({
                  ...f,
                  culture_json: {
                    ...f.culture_json,
                    working_model: v === '__none__' ? '' : v,
                  },
                }))}
              >
                <SelectTrigger className="h-10" data-testid="employer-field-working-model">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {WORKING_MODEL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Location mode</Label>
              <Select
                value={form.culture_json.location_mode || '__none__'}
                onValueChange={(v) => setForm((f) => ({
                  ...f,
                  culture_json: {
                    ...f.culture_json,
                    location_mode: v === '__none__' ? '' : v,
                  },
                }))}
              >
                <SelectTrigger className="h-10" data-testid="employer-field-location-mode">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_MODE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Why join you?</Label>
            <Textarea
              value={form.culture_json.why_join_us}
              onChange={(e) => setForm((f) => ({
                ...f,
                culture_json: { ...f.culture_json, why_join_us: e.target.value },
              }))}
              placeholder="Two or three honest sentences."
              rows={3}
              data-testid="employer-field-why-join"
            />
          </div>
        </CardContent>
      </Card>

      {/* RC 1.61 P1.2: Save/Revert footer is owner/admin-only.
          Recruiter/reviewer/analyst land on a read-only view of
          the same form (no mutate affordances). */}
      {(!memberRole || canManageOrg(memberRole)) ? (
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-ndor-edge">
          {!isCreate && (
            <Button
              variant="ghost" size="sm"
              onClick={onRevert}
              disabled={!isDirty || saving}
              data-testid="employer-revert"
            >
              Revert
            </Button>
          )}
          <Button
            size="sm"
            onClick={onSave}
            disabled={!isDirty || saving}
            data-testid="employer-save"
            className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            {saving
              ? 'Saving…'
              : (isCreate ? 'Create organisation' : 'Save profile')}
          </Button>
        </div>
      ) : null}

      {/* Phase 0.7 Slice S7/S8 — Member management CTA card.
          Renders only once an organisation exists. Sends Admin /
          Recruiter / Reviewer to /employer/members; access posture
          is enforced server-side by the FastAPI shim + S6 RLS. */}
      {!isCreate && (
        <Card className="border-ndor-edge shadow-sm" data-testid="employer-home-members-cta">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
              <UsersIcon className="h-4 w-4 text-ndor-ink-muted" />
              Member management
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-ndor-ink-muted">
              Invite teammates, modify roles, manage suspensions, and review the
              account audit trail.
            </p>
            <Link to="/employer/members" data-testid="employer-home-members-cta-link">
              <Button size="sm" className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted">
                Open members <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* IA cleanup (2026-02-26) — Governance Hub.
          Intentionally lightweight: deep-link surface only. No
          metrics, no usage counters, no billing telemetry, no
          audit telemetry. Operational state lives on
          /employer/account. */}
      {!isCreate && (
        <Card
          className="border-ndor-edge shadow-sm"
          data-testid="employer-home-governance-hub"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
              <ShieldCheckIcon className="h-4 w-4 text-ndor-ink-muted" />
              Governance hub
            </CardTitle>
            <p className="text-xs text-ndor-ink-muted pt-1">
              Workspace authority &amp; access — direct links only.
            </p>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-ndor-edge">
              <li className="py-3 flex items-center justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <UsersIcon className="h-4 w-4 text-ndor-ink-muted mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-ndor-ink">Members</p>
                    <p className="text-[11px] text-ndor-ink-muted">
                      Invitations, role changes, suspensions, audit.
                    </p>
                  </div>
                </div>
                <Link
                  to="/employer/members"
                  data-testid="employer-home-governance-members-link"
                  className="shrink-0"
                >
                  <Button size="sm" variant="outline">
                    Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </Link>
              </li>
              <li className="py-3 flex items-center justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <UserCheckIcon className="h-4 w-4 text-ndor-ink-muted mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-ndor-ink">Reviewers</p>
                    <p className="text-[11px] text-ndor-ink-muted">
                      Assignment scope &amp; reviewer admin.
                    </p>
                  </div>
                </div>
                <Link
                  to="/employer/reviewers"
                  data-testid="employer-home-governance-reviewers-link"
                  className="shrink-0"
                >
                  <Button size="sm" variant="outline">
                    Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </Link>
              </li>
              <li className="py-3 flex items-center justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <ShieldCheckIcon className="h-4 w-4 text-ndor-ink-muted mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-ndor-ink">Roles &amp; permissions</p>
                    <p className="text-[11px] text-ndor-ink-muted">
                      Reference matrix for the four-role authority model.
                    </p>
                  </div>
                </div>
                <Link
                  to="/employer/help/permissions"
                  data-testid="employer-home-governance-permissions-link"
                  className="shrink-0"
                >
                  <Button size="sm" variant="outline">
                    Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </Link>
              </li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
